import {useAppDispatch, useAppSelector} from '@/store';
import {
  setCurrentStep,
  setFormData,
  setValidation,
  resetRewardsData
} from '@/store/slices/rewardsSlice';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useDropdown} from '@/hooks/Dropdown-hook';
import {DROPDOWN_TYPES} from '@/utils/constants/dropdownConstants';
import {getSegmentsList, getRewardDetail} from '@/services/rewards';
import {REWARD_STATUS} from '@/utils/constants/rewardsConstants';
import {createHandleRevoke} from './rewardHandlers';
import {withLoadingAndErrorHandling, showToast} from './rewardHandlerUtils';
import {buildQueryParams} from '@/utils/commons';
import {getProductList} from '@/services/ProductsService/product';
import {extractImageUrls, mapUrlsToImageSlots} from '@/utils/rewardsDisplay';
import {
  buildRewardPayload,
  getDisableDraftFlag as getDisableDraftFlagUtil,
  createHandleDuplicate
} from '../utils/rewardUtils';

import {useMemo, useCallback, useState, useRef} from 'react';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';
import {createDownloadRewardReport} from '@/helpers/rewardsUtils';
import {useQueries, keepPreviousData} from '@tanstack/react-query';
import {useDataTable} from '@/hooks/useDataTable-hook';
import {useRewardsDisplay} from '@/hooks/useRewardsDisplay';
import {
  useNameValidation,
  usePointsValidation,
  useOrderQuantityValidation,
  useBonusProductLimitValidation,
  validateFetchedProductsDetails,
  useRedeemPointsValidation,
  useRewardCashAmountValidation,
  useRewardCardTypeValidation,
  useValueValidation,
  useDigitalValueValidation,
  useDigitalSupplierValidation,
  useQuotaValidation,
  useMaxQtyPerOrderValidation,
  useLoyaltyURLValidation,
  useQuotaDigitalCardValidation,
  useQuotaPhysicalCardValidation
} from '@/containers/Rewards/validations';
import {
  validateStartDate,
  validateEndDate
} from '@/containers/Rewards/RewardAttributes/validations';
import {
  mapRewardProductsToSelected,
  mapRewardProductsToFetched,
  mapProductDisplay,
  PRODUCT_BASED_REWARD_TYPES,
  mapIncludedSegmentGroups,
  mapExcludedSegments,
  buildLoadDraftFormData
} from '@/utils/rewardPayloadUtils';
import {
  hasSegmentErrors,
  getStep3Errors,
  getStep2ValidationResult,
  getRewardTypeValidation,
  getEffectiveLanguages
} from '@/helpers/rewardsUtils';

/**
 * Base hook that contains all common logic shared between useRewards and flow-specific hooks
 * (useRewardsCreate, useRewardsEdit, useRewardsView)
 * This function sets up all the shared hooks, validation functions, and handlers
 * @returns {Object} Common rewards hook state and functions
 */
export const useRewardsBase = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {currentStep, formData, validation, isLoading} = useAppSelector(
    (state) => state.rewards
  );
  const {
    setPageIndex,
    setSortingObj,
    setSearchArry,
    pageIndex,
    sortingObj,
    searchArr,
    handleResetAll
  } = useDataTable();
  const [pageSize, setPageSize] = useState(10);
  const countryCode = useAppSelector((state) => state.countryCode)?.countryCode;
  const {downloadFromBlobUrl} = useAzureBlob();
  const displayContentLanguages = useAppSelector(
    (state) => state.common.languages
  );

  // Fetch reward types from API
  const {dropdownList: rewardTypes, isLoading: rewardTypesLoading} =
    useDropdown({
      dropdownType: DROPDOWN_TYPES.RewardTypes
    });

  // Fetch segments from API
  const {dropdownList: segments, isLoading: segmentsLoading} = useDropdown({
    dropdownType: DROPDOWN_TYPES.RewardSegments
  });

  // Fetch bonus products from API
  const {dropdownList: bonusProducts, isLoading: bonusProductsLoading} =
    useDropdown({
      dropdownType: DROPDOWN_TYPES.BonusProducts
    });

  // Fetch visa cards rewardCardTypes from API
  const {dropdownList: rewardCardTypes, isLoading: rewardCardTypesLoading} =
    useDropdown({
      dropdownType: DROPDOWN_TYPES.RewardCardTypes
    });

  // Validation hooks using centralized validations
  const {onChange: validateName} = useNameValidation();
  const {onChange: validatePoints} = usePointsValidation();
  const {onChange: validateOrderQuantity} = useOrderQuantityValidation();
  const {onChange: validateBonusProductLimit} =
    useBonusProductLimitValidation();
  const {onChange: validateReedemPoints} = useRedeemPointsValidation();
  const {onChange: validateRewardCashAmount} = useRewardCashAmountValidation();
  const {onChange: validateRewardCardType} = useRewardCardTypeValidation();
  const {onChange: validateValue} = useValueValidation();
  const {onChange: validateDigitalValue} = useDigitalValueValidation();
  const {onChange: validateDigitalSupplier} = useDigitalSupplierValidation();
  const {onChange: validateQuota} = useQuotaValidation();
  const {onChange: validateMaxQtyPerOrder} = useMaxQtyPerOrderValidation();
  const {onChange: validateLoyaltyURL} = useLoyaltyURLValidation();
  const {onChange: validateQuotaDigitalCard} = useQuotaDigitalCardValidation();
  const {onChange: validateQuotaPhysicalCard} =
    useQuotaPhysicalCardValidation();

  const queryParams = useMemo(() => {
    return buildQueryParams({
      ...(sortingObj?.column && {
        sortBy: sortingObj.column,
        order: sortingObj.direction
      }),
      pageSize,
      page: pageIndex || 0
    });
  }, [sortingObj?.column, sortingObj?.direction, pageIndex, pageSize]);

  // Fetch segments data using React Query
  const [
    {
      data: segmentsData,
      isLoading: segmentsApiLoading,
      isFetching: segmentsApiFetching
    }
  ] = useQueries({
    queries: [
      {
        queryKey: ['SEGMENTS_LIST', queryParams, searchArr],
        queryFn: () => getSegmentsList(queryParams, searchArr),
        staleTime: 10 * 60 * 1000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData
      }
    ]
  });

  // Rapid page changes can break the keepPreviousData chain, leaving `data`
  // briefly undefined mid-fetch — serve the last good payload during that gap
  // so pageIndex/totalRecords don't drop to 0 and snap the table back to page 1.
  const lastGoodSegmentsDataRef = useRef(null);
  const resolvedSegmentsData = useMemo(() => {
    const hasRows = segmentsData?.data?.length > 0;
    if (hasRows || !segmentsApiFetching) {
      lastGoodSegmentsDataRef.current = segmentsData;
      return segmentsData;
    }
    return lastGoodSegmentsDataRef.current ?? segmentsData;
  }, [segmentsData, segmentsApiFetching]);

  const lastKnownSegmentsTotalRef = useRef(0);
  const segmentsTotal = useMemo(() => {
    const parsed = Number(resolvedSegmentsData?.totalRecords);
    if (Number.isFinite(parsed) && parsed > 0) {
      lastKnownSegmentsTotalRef.current = parsed;
      return parsed;
    }
    return resolvedSegmentsData?.data?.length > 0
      ? lastKnownSegmentsTotalRef.current
      : 0;
  }, [resolvedSegmentsData]);

  // Load draft from API
  const loadDraft = useCallback(
    async (rewardIdToLoad) => {
      if (!rewardIdToLoad) return;

      await withLoadingAndErrorHandling({
        dispatch,
        operation: async () => {
          const resp = await getRewardDetail(rewardIdToLoad);
          const data = resp?.data || resp;
          const apiRewardType =
            data?.rewardType?.toUpperCase?.() || data?.rewardType || '';
          const rewardProducts = data?.rewardProducts || [];

          const formDataToSet = buildLoadDraftFormData({
            data,
            apiRewardType,
            rewardIdToLoad,
            mappedProducts: mapRewardProductsToSelected(rewardProducts),
            mappedFetchedProducts: mapRewardProductsToFetched(rewardProducts),
            mappedProductDisplay: PRODUCT_BASED_REWARD_TYPES.includes(
              apiRewardType
            )
              ? mapProductDisplay(rewardProducts, data, apiRewardType)
              : {},
            mappedSegments: mapIncludedSegmentGroups(
              data?.includedSegmentGroups
            ),
            mappedExcludedSegments: mapExcludedSegments(data?.excludedSegments)
          });

          dispatch(setFormData(formDataToSet));

          // Fetch default images for products with useDefaultImage: true
          if (PRODUCT_BASED_REWARD_TYPES.includes(apiRewardType)) {
            const defaultImageProducts = rewardProducts.filter(
              (p) => p.defaultImage
            );
            if (defaultImageProducts.length > 0) {
              const updatedProductDisplay = {
                ...formDataToSet.display.productDisplay
              };
              await Promise.all(
                defaultImageProducts.map(async (p) => {
                  try {
                    const qp = buildQueryParams({pageSize: 1, page: 0});
                    const filters = [
                      {
                        field: 'productId',
                        type: 'string',
                        value: String(p.productId)
                      }
                    ];
                    const resp = await getProductList(qp, filters);
                    const respData = resp?.data ?? [];
                    const items = Array.isArray(respData)
                      ? respData
                      : (respData?.items ?? []);
                    const item = items?.[0] || null;
                    if (item) {
                      const urls = extractImageUrls(item);
                      if (urls?.length) {
                        updatedProductDisplay[p.productId] = {
                          ...updatedProductDisplay[p.productId],
                          images: mapUrlsToImageSlots(urls)
                        };
                      }
                    }
                  } catch {
                    // Silently ignore - default images will not show
                  }
                })
              );
              dispatch(
                setFormData({
                  display: {productDisplay: updatedProductDisplay}
                })
              );
            }
          }
        },
        errorMessage: 'Failed to load draft'
      });
    },
    [dispatch]
  );

  // Handle form field changes
  const handleFormChange = useCallback(
    (field, value) => {
      dispatch(setFormData({[field]: value}));
    },
    [dispatch]
  );

  // Handle fetched products update
  const handleFetchedProductsUpdate = useCallback(
    (products) => {
      dispatch(setFormData({fetchedProducts: products}));
    },
    [dispatch]
  );

  // Handle validation
  const handleValidation = useCallback(
    (field, isValid) => {
      dispatch(setValidation({[`${field}Valid`]: isValid}));
    },
    [dispatch]
  );

  // Unified validation functions that return isValid and optionally update validation state
  const getRewardTypeValidationLocal = (
    nameError,
    rewardTypeValid,
    updateState = false
  ) => {
    const validators = {
      validateOrderQuantity,
      validateBonusProductLimit,
      validateFetchedProductsDetails,
      validateReedemPoints,
      validateRewardCashAmount,
      validateRewardCardType,
      validatePoints,
      validateValue,
      validateDigitalValue,
      validateDigitalSupplier,
      validateLoyaltyURL
    };

    const result = getRewardTypeValidation(
      formData,
      nameError,
      rewardTypeValid,
      validators
    );

    if (updateState && result.errors) {
      Object.entries(result.errors).forEach(([key, error]) => {
        if (key !== 'validationKey') {
          handleValidation(key, !!error);
        } else {
          // Handle special case for PepVoucherProduct validationKey
          const validationKey = result.errors.validationKey;
          const selectedProductsError = result.errors.selectedProducts;
          if (validationKey && selectedProductsError !== undefined) {
            handleValidation(validationKey, selectedProductsError);
          }
        }
      });
    }

    return result.isValid;
  };

  // Display step logic moved to dedicated hook (needs to be defined early for validation functions)
  const displayApi = useRewardsDisplay({
    dispatch,
    formData,
    handleFormChange,
    countryCode,
    displayContentLanguages,
    currentStep
  });

  // Helper function to validate step 1 (setup step)
  const validateStep1 = () => {
    const nameError = validateName(formData.name);
    const rewardTypeValid = formData.rewardType?.length > 0;

    // Update validation state
    handleValidation('name', !!nameError);
    handleValidation('rewardType', !rewardTypeValid);

    return getRewardTypeValidationLocal(nameError, rewardTypeValid, true);
  };

  // Shared helper for step 2 validation (pure: no dispatches)
  const getStep2ValidationResultLocal = () => {
    return getStep2ValidationResult(
      formData,
      countryCode,
      displayContentLanguages,
      displayApi
    );
  };

  // Helper function to validate step 2 (display step)
  const validateStep2 = () => {
    return getStep2ValidationResultLocal().isValid;
  };

  // Shared helper for step 3 validation (pure: no dispatches)
  const getStep3ErrorsLocal = () => {
    const validators = {
      validateStartDate,
      validateEndDate,
      validateQuota,
      validateQuotaDigitalCard,
      validateQuotaPhysicalCard,
      validateMaxQtyPerOrder
    };
    return getStep3Errors(formData, validators);
  };

  // Helper function to validate step 3 (attributes step)
  const validateStep3 = () => {
    const {
      startDateError,
      endDateError,
      quotaError,
      quotaDigitalCardError,
      quotaPhysicalCardError,
      maxQtyPerOrderError,
      shouldValidateQuota,
      shouldValidateVisaCardBothQuota,
      shouldValidateMaxQtyPerOrder,
      alertDisplayCountError,
      shouldValidateAlertDisplayCount
    } = getStep3ErrorsLocal();

    // Update validation state
    handleValidation('startDate', !!startDateError);
    handleValidation('endDate', !!endDateError);
    if (shouldValidateQuota) {
      handleValidation('quota', !!quotaError);
    }
    if (shouldValidateVisaCardBothQuota) {
      handleValidation('quotaDigitalCard', !!quotaDigitalCardError);
      handleValidation('quotaPhysicalCard', !!quotaPhysicalCardError);
    }
    if (shouldValidateMaxQtyPerOrder) {
      handleValidation('maxQtyPerOrder', !!maxQtyPerOrderError);
    }
    if (shouldValidateAlertDisplayCount) {
      handleValidation('alertDisplayCount', !!alertDisplayCountError);
    }

    // Step is valid only if there are no date, inventory, or alertDisplayCount errors
    return (
      !startDateError &&
      !endDateError &&
      !quotaError &&
      !quotaDigitalCardError &&
      !quotaPhysicalCardError &&
      !maxQtyPerOrderError &&
      !alertDisplayCountError
    );
  };

  // Unified validation function using the existing validation hook
  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return validateStep1();
      case 2:
        return validateStep2();
      case 3:
        return validateStep3();
      case 4:
        return true;
      default:
        return true;
    }
  };

  // Helper function to check errors for step 1 (setup step)
  const hasStep1Errors = () => {
    const nameError = validateName(formData.name);
    const rewardTypeValid = formData.rewardType?.length > 0;
    const segmentError = hasSegmentErrors(formData);
    const isValid = getRewardTypeValidationLocal(
      nameError,
      rewardTypeValid,
      false
    );
    return !isValid || segmentError;
  };

  // Helper function to check errors for step 2 (display step)
  const hasStep2Errors = () => {
    return !getStep2ValidationResultLocal().isValid;
  };

  // Helper function to check errors for step 3 (attributes step)
  const hasStep3Errors = () => {
    const {
      startDateError,
      endDateError,
      quotaError,
      quotaDigitalCardError,
      quotaPhysicalCardError,
      maxQtyPerOrderError,
      alertDisplayCountError
    } = getStep3ErrorsLocal();

    return (
      !!startDateError ||
      !!endDateError ||
      !!quotaError ||
      !!quotaDigitalCardError ||
      !!quotaPhysicalCardError ||
      !!maxQtyPerOrderError ||
      !!alertDisplayCountError
    );
  };

  // Check if form has errors for current step
  const hasFormErrors = () => {
    switch (currentStep) {
      case 1:
        return hasStep1Errors();
      case 2:
        return hasStep2Errors();
      case 3:
        return hasStep3Errors();
      case 4:
        return false;
      default:
        return false;
    }
  };

  // Build payload for API
  const buildPayload = useCallback(
    (status = REWARD_STATUS.DRAFT) => {
      // If the reward is already ACTIVE, keep ACTIVE status instead of changing to PUBLISHED
      const effectiveStatus =
        status === REWARD_STATUS.PUBLISHED &&
        formData?.status === REWARD_STATUS.ACTIVE
          ? REWARD_STATUS.ACTIVE
          : status;

      const languages = getEffectiveLanguages(
        countryCode,
        displayContentLanguages
      );
      return buildRewardPayload({
        formData,
        // segment list ({segmentId, segmentName}) used to send the known segment ids
        segments: resolvedSegmentsData?.data || [],
        status: effectiveStatus,
        languages
      });
    },
    [formData, resolvedSegmentsData, countryCode, displayContentLanguages]
  );

  // Get disable draft flag
  const getDisableDraftFlag = useCallback(() => {
    return getDisableDraftFlagUtil(formData);
  }, [formData]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    dispatch(resetRewardsData());
    navigate(ROUTES.REWARD_CATALOG);
  }, [dispatch, navigate]);

  // Handle revoke reward
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleRevoke = useCallback(
    createHandleRevoke({formData, countryCode, dispatch}),
    [formData, countryCode, dispatch]
  );

  // Handle download reward report
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const downloadRewardReport = useCallback(
    createDownloadRewardReport({dispatch, downloadFromBlobUrl}),
    [dispatch, downloadFromBlobUrl]
  );

  // Handle duplicate
  const handleDuplicate = createHandleDuplicate({
    formData,
    dispatch,
    navigate,
    showToast,
    createRoute: ROUTES.REWARDS_CREATE
  });

  // Expose setter for directly setting a step from UI
  const setStep = useCallback(
    (step) => {
      dispatch(setCurrentStep(step));
    },
    [dispatch]
  );

  // Reset reward conditions
  const resetRewardConditions = useCallback(() => {
    dispatch(
      setFormData({
        rewardType: '',
        selectedProducts: [],
        fetchedProducts: [],
        orderQty: '',
        limitOfProductQty: '',
        cardType: '',
        rewardCashAmount: '',
        redeemPoints: '',
        digitalCodeAmount: '',
        digitalCodePartner: '',
        selectedPepVouchersProducts: [],
        value: '',
        loyaltyUrl: '',
        couponBasedLoyaltyReward: false
      })
    );
    dispatch(
      setValidation({
        rewardTypeValid: undefined,
        selectedProductsValid: undefined,
        orderQtyValid: undefined,
        limitOfProductQtyValid: undefined,
        cardTypeValid: undefined,
        rewardCashAmountValid: undefined,
        redeemPointsValid: undefined,
        selectedPepVouchersProductsValid: undefined,
        valueValid: undefined,
        digitalCodeAmountValid: undefined,
        digitalCodePartnerValid: undefined,
        loyaltyUrlValid: undefined
      })
    );
  }, [dispatch]);

  return {
    // State
    currentStep,
    formData,
    validation,
    isLoading,

    // Data
    rewardTypes,
    segments,
    bonusProducts,
    rewardCardTypes,
    rewardTypesLoading,
    segmentsLoading,
    bonusProductsLoading,
    rewardCardTypesLoading,
    pepVouchersOrProductsLoading: bonusProductsLoading,
    segmentsApiData: resolvedSegmentsData?.data || [],
    segmentsTotal,
    segmentsApiLoading,
    setPageIndex,
    setSortingObj,
    setSearchArry,
    pageIndex,
    sortingObj,
    searchArr,
    handleResetAll,
    pageSize,
    setPageSize,

    // Validation functions
    validateName,
    validatePoints,
    validateOrderQuantity,
    validateBonusProductLimit,
    validateReedemPoints,
    validateRewardCashAmount,
    validateRewardCardType,
    validateValue,
    validateDigitalValue,
    validateDigitalSupplier,
    validateLoyaltyURL,
    hasFormErrors,
    validateCurrentStep,
    validateStep1,
    validateStep2,
    validateStep3,
    hasStep1Errors,
    hasStep2Errors,
    hasStep3Errors,
    getStep2ValidationResultLocal,
    getStep3ErrorsLocal,
    getRewardTypeValidationLocal,

    // Actions
    handleFormChange,
    handleFetchedProductsUpdate,
    handleValidation,
    handleCancel,
    handleRevoke,
    handleDuplicate,
    downloadRewardReport,
    resetRewardConditions,
    setStep,
    loadDraft,
    buildPayload,
    getDisableDraftFlag,
    navigate,
    dispatch,

    // Display step API
    ...displayApi
  };
};
